import { Component, Inject, Input } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Task, TaskStatus } from '@contracts/interfaces/task.interface';
import { TaskService } from '../../services/task.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerDialogService } from '../customer/customer.service.dialog';
import { InvoicesService } from '../../services/invoices.service';
import { CustomerInterface } from '@contracts/interfaces/customer.interface';
import { PDFService } from 'src/app/services/pdf.service';
import { ChatGPTService } from 'src/app/services/chatgpt.service';
import { LineItem } from '@contracts/interfaces/lineitem.interface';
import { InvoiceInterface, InvoiceStatus } from '@contracts/interfaces/invoice.interface';
import { PartsOrder, PartsOrderStatus } from '@contracts/interfaces/parts-order.interface';
import { PartsOrderService } from '../../services/parts-order.service';
import { PartsOrderDialog } from '../parts-order-dialog/parts-order.dialog';
import { TaskCompleteConfirmDialog } from '../task-complete-confirm/task-complete-confirm.dialog';
import { forkJoin } from 'rxjs';
import { SendSMSServiceDialog } from '../send-sms/send-sms.service.dialog';

interface PreviewUpdate {
  type: string;
  text: string;
  date: Date;
}

type SmsUpdateTemplate = 'received' | 'in-progress' | 'parts' | 'ready' | 'payment' | 'review';

@Component({
  selector: 'ap-task-dialog',
  templateUrl: 'task.dialog.html',
  styleUrls: ['./task.dialog.scss']
})
export class TaskDialogComponent {

  public TaskStatus = TaskStatus;
  waitingForPartsUpdating = false;
  waitingForPartsError = '';
  public PartsOrderStatus = PartsOrderStatus;
  public list: number[];

  public currentIndex: number = null;

  public isLoading = false;

  public task: Task | undefined;

  public newComment: string = "";
  public nextAction: string = "";
  public nextActionDuration: string = "";
  public nextActionBlocker: string = "";
  public aiInvoiceLoading = false;
  public aiInvoiceError: string | null = null;
  public invoiceCreateLoading = false;
  public partsOrders: PartsOrder[] = [];
  public partsLoading = false;
  public partsError: string | null = null;
  public previewUpdates: PreviewUpdate[] = [];
  public customSmsMessage = '';

  public getTaskAge(dateInput: Date | string): string {
    if (!dateInput) {
      return '';
    }

    const created = new Date(dateInput);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      const remHours = hours % 24;
      return `${days}d ${remHours}h ago`;
    }

    if (hours > 0) {
      const remMinutes = minutes % 60;
      return `${hours}h ${remMinutes}m ago`;
    }

    return `${Math.max(minutes, 1)}m ago`;
  }

  public trackByLogEntry(index: number) {
    return index;
  }

  public get updatePreviewLog(): PreviewUpdate[] {
    return [
      ...(this.task?.log || []),
      ...this.previewUpdates
    ];
  }

  constructor(
      @Inject(MAT_DIALOG_DATA) public data: any,
      public dialogRef: MatDialogRef<TaskDialogComponent>,
      public dialog: MatDialog,
      public taskService: TaskService,
      private router: Router,
      private customerDialogService: CustomerDialogService,
      private invoiceService: InvoicesService,
      private partsOrderService: PartsOrderService,
      private pdfService: PDFService,
      private chatGPTService: ChatGPTService,
      private sendSMSServiceDialog: SendSMSServiceDialog
      ) {
        this.task = data.task;
        if (this.task && !this.task.status) {
          this.task.status = TaskStatus.InProgress;
        }
        this.list = data.list;
        this.currentIndex = data.currentIndex;
        this.syncInvoiceLink(this.task);
        this.loadPartsOrders();
      }

      openSmsUpdateDialog(template: SmsUpdateTemplate) {
        if (!this.task?.customer) {
          return;
        }

        const message = this.buildSmsUpdateMessage(template);

        this.sendSMSServiceDialog.open(this.task.customer, message, (job: any, sentMessage?: string) => {
          if (!job) {
            return;
          }

          if (!this.task.log) {
            this.task.log = [];
          }

          this.task.log.push({
            type: this.getSmsUpdateLogType(template),
            text: sentMessage !== undefined ? sentMessage : message,
            date: new Date()
          });

          this.taskService.update(this.task).subscribe(() => {
            this.refresh();
          });
        });
      }

      sendCustomSmsUpdate() {
        const message = this.customSmsMessage.trim();

        if (!this.task?.customer || !message) {
          return;
        }

        this.sendSMSServiceDialog.open(this.task.customer, message, (job: any, sentMessage?: string) => {
          if (!job) {
            return;
          }

          if (!this.task.log) {
            this.task.log = [];
          }

          this.task.log.push({
            type: 'sms-custom',
            text: sentMessage !== undefined ? sentMessage : message,
            date: new Date()
          });
          this.customSmsMessage = '';

          this.taskService.update(this.task).subscribe(() => {
            this.refresh();
          });
        });
      }

      hasSmsUpdateBeenSent(template: SmsUpdateTemplate) {
        const logType = this.getSmsUpdateLogType(template);

        return !!this.task?.log?.some(entry => entry.type === logType);
      }

      private getSmsUpdateLogType(template: SmsUpdateTemplate) {
        return `sms-${template}`;
      }

      private buildSmsUpdateMessage(template: SmsUpdateTemplate) {
        const customerName = this.getCustomerFirstName();
        const deviceName = this.task?.subject || 'your device';

        const messages = {
          received: `Hi ${customerName}, thanks for booking your repair with Repair Lab.

We'll keep you updated by SMS as your repair progresses.

We may also contact you from our mobile number. You're welcome to call or message us if you have any questions about your repair.

📱 Add Repair Lab to your contacts:
repairlab.com.au/add-my-contact`,
          'in-progress': `Hi ${customerName}, we have started work on ${deviceName}. We will let you know once we have the next update.`,
          parts: `Hi ${customerName}, ${deviceName} needs parts before we can continue. We will update you when they arrive.`,
          ready: `Hi ${customerName}, ${deviceName} is ready for collection.`,
          payment: `Hi ${customerName}, ${deviceName} is ready. We will send through the payment amount shortly.`,
          review: `Hi ${customerName}, thanks for choosing Repair Lab. If you were happy with our service, we'd appreciate a quick Google review:

https://g.page/r/CR-ekYinBCYvEAg/review`
        };

        return messages[template];
      }

      private getCustomerFirstName() {
        const name = this.task?.customer?.nick || this.task?.customer?.name || '';
        const firstName = name.trim().split(/\s+/)[0];

        return firstName || 'there';
      }

  public get isQueued() {
    return this.task?.status === TaskStatus.Queued;
  }

  private loadPartsOrders() {
    this.partsOrders = [];
    this.partsError = null;
    this.partsLoading = false;

    const taskId = this.task?.id ? Number(this.task.id) : null;
    if (!taskId) {
      return;
    }

    this.partsLoading = true;
    this.partsOrderService.list().subscribe({
      next: (orders) => {
        this.partsOrders = (orders || []).filter(order => Number(order.taskId) === taskId);
        this.partsLoading = false;
      },
      error: () => {
        this.partsLoading = false;
        this.partsError = 'Unable to load parts orders.';
      }
    });
  }

  openPartsOrder(order?: PartsOrder) {
    const baseOrder: PartsOrder = order || {
      title: '',
      status: PartsOrderStatus.ORDERED,
      quantity: 1,
      taskId: this.task?.id
    };

    const dialogRef = this.dialog.open(PartsOrderDialog, {
      width: '720px',
      data: { order: baseOrder }
    });

    dialogRef.afterClosed().subscribe((result?: PartsOrder) => {
      if (!result) {
        return;
      }

      const payload = order
        ? { ...order, ...result }
        : { ...result, taskId: result.taskId ?? this.task?.id };

      const request$ = order
        ? this.partsOrderService.update(payload)
        : this.partsOrderService.create(payload);

      request$.subscribe(() => this.loadPartsOrders());
    });
  }

  goToPartsOrdered() {
    this.router.navigate(['/books/parts-ordered']);
  }

      next() {
        this.task = null;
        
        this.currentIndex++;
  
        if (this.currentIndex > this.list.length-1) {
          this.dialogRef.close();
        } else {
          this.taskService.get(this.list[this.currentIndex]).subscribe((task: Task) => {
            this.task = task;
            this.syncInvoiceLink(this.task);
            this.loadPartsOrders();
          });
        }
      }
  
      previous() {
        this.task = null;
  
        this.currentIndex--;
  
        if (this.currentIndex < 0) {
          this.dialog.closeAll();
        } else {
          this.taskService.get(this.list[this.currentIndex]).subscribe((task: Task) => {
            this.task = task;
            this.syncInvoiceLink(this.task);
            this.loadPartsOrders();
          });
        }
      }

      save() {
        if (this.task != undefined) {
          this.taskService.update(this.task).subscribe(r=> {});
        }
      }
    
      refresh() {
        this.taskService.find({id: this.task.id}).subscribe((response: any) => {
          this.task = response[0];
          this.syncInvoiceLink(this.task);
          this.loadPartsOrders();

          if (this.task.invoice == undefined) {
            this.task.invoice = this.buildRepairInvoiceBase(this.task.customer);
          }

          this.isLoading = false;
        })
      }

      delete() {
        if (this.task != undefined) {
          this.taskService.delete(this.task).subscribe(r => {
            this.router.navigate(["tasks"]);
          });
        }
      }
    
      addComment() {
        if (this.task != undefined) {
          if (this.task.log == undefined) {
            this.task.log = [];
          }
    
          this.task.log.push({
            type: "comment",
            text: this.newComment,
            date: new Date()
          })
    
          this.taskService.update(this.task).subscribe(r=> {
            this.newComment = "";
            this.refresh();
          });
        }
      }

      addNextActionComment() {
        if (this.task == undefined) {
          return;
        }

        const parts = [
          `What's the next action?\n${this.nextAction || '-'}`,
          `How long will that next action take?\n${this.nextActionDuration || '-'}`,
          `Is anything blocking it?\n${this.nextActionBlocker || '-'}`
        ];

        if (this.task.log == undefined) {
          this.task.log = [];
        }

        this.task.log.push({
          type: "comment",
          text: parts.join('\n\n'),
          date: new Date()
        });

        this.taskService.update(this.task).subscribe(r=> {
          this.nextAction = "";
          this.nextActionDuration = "";
          this.nextActionBlocker = "";
          this.refresh();
        });
      }

      removeComment(index: number) {
        if (this.task == undefined || !this.task.log?.length) {
          return;
        }

        this.task.log.splice(index, 1);
        this.taskService.update(this.task).subscribe(() => {
          this.refresh();
        });
      }
    
      toggleWaitingForParts() {
        if (!this.task || this.waitingForPartsUpdating) return;
        const task = this.task;
        const previous = task.waitingForParts;
        task.waitingForParts = !previous;
        this.waitingForPartsUpdating = true;
        this.waitingForPartsError = '';
        this.taskService.update(task).subscribe({
          next: () => {
            this.waitingForPartsUpdating = false;
            this.refresh();
          },
          error: () => {
            task.waitingForParts = previous;
            this.waitingForPartsUpdating = false;
            this.waitingForPartsError = 'Could not save the waiting for parts flag. Please try again.';
          }
        });
      }

      updateStatus(status: TaskStatus) {
        if (!this.task) {
          return;
        }

        this.task.status = status;
        this.taskService.update(this.task).subscribe(() => {
          this.refresh();
        });
      }

      updateStatusCompleted() {
        if (!this.task) {
          return;
        }

        const isCompleted = this.task.status === TaskStatus.Completed;
        if (isCompleted) {
          this.completeTask(false);
          return;
        }

        const partsToReceive = this.partsOrders.filter(order => order.status !== PartsOrderStatus.RECEIVED);
        if (!partsToReceive.length) {
          this.completeTask(false);
          return;
        }

        const dialogRef = this.dialog.open(TaskCompleteConfirmDialog, {
          width: '520px',
          data: {
            taskId: this.task.id,
            partsOrders: partsToReceive
          }
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
          if (confirmed) {
            this.completeTask(true, partsToReceive);
          }
        });

      }

      private completeTask(markPartsReceived: boolean, partsToReceive: PartsOrder[] = []) {
        if (!this.task) {
          return;
        }

        const isCompleted = this.task.status === TaskStatus.Completed;
        const shouldPromptReview = !isCompleted && this.shouldPromptForReview(this.task.customer);
        this.task.status = isCompleted ? TaskStatus.InProgress : TaskStatus.Completed;

        this.taskService.update(this.task).subscribe(() => {
          const afterTaskSaved = () => {
            this.refresh();

            if (shouldPromptReview && this.task?.customer?._id) {
              this.customerDialogService.open(
                this.task.customer,
                '',
                (r: any) => {
                  if (r && this.task && (r as CustomerInterface)._id) {
                    this.task.customer = r;
                  }
                },
                (dialogRef) => {
                  dialogRef.componentInstance.tab = 'review';
                }
              );
            }
          };

          if (!markPartsReceived || !partsToReceive.length) {
            afterTaskSaved();
            return;
          }

          const receivedAt = new Date();
          const updates = partsToReceive.map(order => this.partsOrderService.update({
            ...order,
            status: PartsOrderStatus.RECEIVED,
            receivedDate: receivedAt
          }));

          forkJoin(updates).subscribe(() => {
            afterTaskSaved();
          });
        });
      }

      queueTask() {
        this.updateStatus(TaskStatus.Queued);
      }

      unqueueTask() {
        this.updateStatus(TaskStatus.InProgress);
      }

      updateType(type: string) {
        if (this.task != undefined) {
          this.task.type = type;
          this.taskService.update(this.task).subscribe(r => {
            this.refresh();
          })
        }
      }
    
      openCustomerDialog(customer: CustomerInterface) {
        this.customerDialogService.open(customer, "", (r: any) => {
          if (r !== undefined && this.task != undefined) {
            this.task.customer = r;
          }
        });
      }
    
      test() {
        if (this.task != undefined) {
          console.log(this.task.customer);
        }
      }

      printTask() {
        this.pdfService.print("task-sheet", this.task, (r) => {

        });
      }

      changeTaskType(newType: string) {
        if (this.task != undefined) {
          this.task.type = newType;
          this.taskService.update(this.task).subscribe(r => {
            this.refresh();
          });
        }
      }

      generateInvoiceFromTask() {
        if (!this.task || this.aiInvoiceLoading || this.invoiceCreateLoading) {
          return;
        }

        this.aiInvoiceLoading = true;
        this.aiInvoiceError = null;

        const prompt = this.buildInvoicePrompt();
        const instruction = `
You are an invoicing assistant. Given task details, return JSON only (no prose, no code fences).
Shape:
{
  "notes": "optional short note",
  "lineItems": [
    { "item": "Service", "desc": "Work performed", "qty": 1, "price": 100 }
  ]
}
Rules:
- Prices are AUD numbers expressed in cents (integer, no currency symbols).
- Always include a Service line summarising labour.
- Each part is its own line item.
- qty is numeric (e.g., 1, 1.5).`;

        const message = `${instruction}\n\nTask details:\n${prompt}`;

        this.chatGPTService.prompt(message, 'gpt-4o-mini').subscribe({
          next: (resp: any) => {
            const draft = this.parseInvoiceDraft(resp?.content ?? resp, this.task.description || this.task.subject || 'Task work');
            this.createInvoiceForTask(draft, true);
          },
          error: (err) => {
            this.aiInvoiceLoading = false;
            this.aiInvoiceError = err?.error?.message ?? 'Failed to generate invoice from task.';
          }
        });
      }

      createInvoiceForTask(draft?: { lineItems: LineItem[]; notes?: string }, fromAI: boolean = false) {
        if (!this.task || this.invoiceCreateLoading) {
          return;
        }

        if (fromAI) {
          this.aiInvoiceLoading = true;
        } else {
          this.invoiceCreateLoading = true;
        }
        this.aiInvoiceError = null;

        let invoice: InvoiceInterface = this.buildRepairInvoiceBase(this.task.customer);
        invoice.lineItems = invoice.lineItems || [];
        invoice.custom = this.task?.id ? `task-${this.task.id}` : invoice.custom;
        invoice.additionalData = {
          ...invoice.additionalData,
          taskId: this.task?.id,
          taskSubject: this.task?.subject
        };

        if (draft?.lineItems?.length) {
          invoice.lineItems = draft.lineItems.map((li) => ({
            item: li?.item || 'Service',
            desc: li?.desc || this.task?.description || this.task?.subject || 'Task work',
            qty: Number(li?.qty) || 1,
            price: this.toNumber(li?.price)
          }));
        } else {
          invoice.lineItems = [
            {
              item: 'Service',
              desc: this.task?.description || this.task?.subject || 'Task work',
              qty: 1,
              price: this.toNumber(this.task?.quote)
            }
          ];
        }

        invoice = this.invoiceService.validate(invoice);

        const createWithNumber = () => {
          this.invoiceService.create(invoice).subscribe({
            next: (created: InvoiceInterface) => {
              const createdInvoice: InvoiceInterface = (created as any)?.invoice || created || invoice;
              const validated = this.invoiceService.validate(createdInvoice);
              this.persistInvoiceOnTask(validated, fromAI);
            },
            error: (err) => {
              this.finishInvoiceLoad(fromAI);
              this.aiInvoiceError = err?.error?.message ?? 'Failed to create invoice.';
            }
          });
        };

        if (!invoice.number) {
          this.invoiceService.getAvailableInvoiceNumber().subscribe({
            next: (r: any) => {
              invoice.number = r?.number ?? invoice.number;
              createWithNumber();
            },
            error: () => {
              this.finishInvoiceLoad(fromAI);
              this.aiInvoiceError = 'Failed to fetch invoice number.';
            }
          });
        } else {
          createWithNumber();
        }
      }

      openInvoice() {
        const invoiceNumber = this.task?.invoice?.number || (this.task as any)?.invoiceNumber;
        if (!invoiceNumber) {
          return;
        }

        this.invoiceCreateLoading = true;
        this.invoiceService.get(invoiceNumber).subscribe({
          next: (invoice) => {
            if (!invoice) {
              this.invoiceCreateLoading = false;
              this.detachInvoiceLink(this.task, true);
              return;
            }
            const validated = this.invoiceService.validate(invoice);
            this.invoiceCreateLoading = false;
            this.invoiceService.openInvoiceDialog(validated, () => {
              this.refresh();
            });
          },
          error: () => {
            this.invoiceCreateLoading = false;
            this.detachInvoiceLink(this.task, true);
          }
        });
      }

      private buildInvoicePrompt(): string {
        const parts = [];
        if (this.task?.id) {
          parts.push(`Task #: ${this.task.id}`);
        }
        if (this.task?.subject) {
          parts.push(`Subject: ${this.task.subject}`);
        }
        if (this.task?.type) {
          parts.push(`Type: ${this.task.type}`);
        }
        if (this.task?.description) {
          parts.push(`Description: ${this.task.description}`);
        }
        if (this.task?.quote !== undefined && this.task?.quote !== null) {
          parts.push(`Quote: ${this.task.quote}`);
        }
        if (this.task?.customer?.name) {
          parts.push(`Customer: ${this.task.customer.name}`);
        }
        return parts.join('\n');
      }

      private parseInvoiceDraft(content: any, fallbackDesc: string): { lineItems: LineItem[]; notes?: string } {
        let text = '';

        if (typeof content === 'string') {
          text = content;
        } else if (content?.content) {
          text = content.content;
        }

        const cleaned = text.replace(/```json|```/gi, '').trim();

        let parsed: any;
        try {
          const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
          parsed = JSON.parse(jsonMatch ? jsonMatch[0] : cleaned);
        } catch (_) {
          parsed = null;
        }

        const rawItems: any[] = parsed?.lineItems ?? [];
        const lineItems: LineItem[] = rawItems
          .map((li) => ({
            item: li?.item || 'Service',
            desc: li?.desc || fallbackDesc,
            qty: Number(li?.qty) || 1,
            price: this.toNumber(li?.price)
          }))
          .filter((li) => !!li.desc);

        if (!lineItems.length) {
          lineItems.push({
            item: 'Service',
            desc: fallbackDesc,
            qty: 1,
            price: 0
          });
        }

        return {
          lineItems,
          notes: parsed?.notes
        };
      }

      private toNumber(value: any): number {
        const num = Number(value);
        if (isNaN(num)) {
          return 0;
        }
        return Math.round(num * 100) / 100;
      }

      private finishInvoiceLoad(fromAI: boolean) {
        if (fromAI) {
          this.aiInvoiceLoading = false;
        } else {
          this.invoiceCreateLoading = false;
        }
      }

      private persistInvoiceOnTask(invoice: InvoiceInterface, fromAI: boolean) {
        if (!this.task) {
          this.finishInvoiceLoad(fromAI);
          return;
        }

        this.task.invoice = invoice;
        (this.task as any).invoiceNumber = invoice.number;

        const openDialog = () => {
          this.invoiceService.openInvoiceDialog(invoice, () => {
            this.refresh();
          });
        };

        this.finishInvoiceLoad(fromAI);
        openDialog();

        this.taskService.update(this.task).subscribe({
          next: () => {},
          error: () => {
            this.aiInvoiceError = 'Invoice created but failed to attach to task.';
          }
        });
      }

      private syncInvoiceLink(task: Task | undefined) {
        if (!task) {
          return;
        }

        const invoiceNumber = (task as any).invoiceNumber || task.invoice?.number;

        if (task.invoice?.number && !(task as any).invoiceNumber) {
          (task as any).invoiceNumber = task.invoice.number;
        }

        if (!invoiceNumber) {
          return;
        }

        this.invoiceService.get(invoiceNumber).subscribe({
          next: (inv) => {
            if (inv) {
              task.invoice = this.invoiceService.validate(inv);
              (task as any).invoiceNumber = task.invoice.number;
            } else {
              this.detachInvoiceLink(task, true);
            }
          },
          error: () => {
            this.detachInvoiceLink(task, true);
          }
        });
      }

      private detachInvoiceLink(task: Task | undefined, persist: boolean = false) {
        if (!task) {
          return;
        }

        task.invoice = undefined;
        delete (task as any).invoiceNumber;

        if (persist && task.id) {
          this.taskService.update(task).subscribe({
            next: () => {},
            error: () => {}
          });
        }
      }

      private buildRepairInvoiceBase(customer: CustomerInterface | null): InvoiceInterface {
        return {
          type: 'repair',
          status: InvoiceStatus.Unpaid,
          customer,
          number: null,
          custom: '',
          dateCreated: new Date(),
          dateDue: new Date(),
          subTotal: 0,
          total: 0,
          paymentsMade: [],
          pdf: {
            file: 'invoice-repair-lab'
          },
          lineItems: [
            {
              item: '',
              desc: '',
              qty: 1,
              price: 0
            }
          ],
          gst: 0,
          isGSTIncluded: true,
          communicationLog: []
        };
      }

      private shouldPromptForReview(customer?: CustomerInterface | null): boolean {
        if (!customer?._id) {
          return false;
        }

        const status = customer.googleReviewStatus;
        const hasSentReview =
          status === 'requested' ||
          status === 'left';
        const skipRequested = status === 'do_not_request';
        const hasSmsHistory = Array.isArray(customer.googleReviewSMS) && customer.googleReviewSMS.length > 0;

        return !hasSentReview && !skipRequested && !hasSmsHistory;
      }
}
